import { ifAll, ifAny } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrCSSZSubmissionLongtermCaregivingDetailEntity,
    PrCSSZSubmissionLongtermCaregivingEntity,
    PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity,
    PrMpsvFamilyRelationEntity,
    PrWorkedInDaysIntervalEntity,
    PrWorkLeaveDaysIntervalEntity,
    PrWorkScheduleDaysIntervalEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { addCareReceiverDefinitions } from "@pages/payroll/socialEvents/SocialEventsDef";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import {
    addBasicBenefitRequestGroup,
    addCaregivingActions,
    addCaregivingIntervalFields,
    addCommonNEMPRIDefinitions,
    addCSSZSwitchDef,
    addMaternityRelatedFieldDefinitions,
    addReturnToWorkFields,
    addStudyFieldDefinitions,
    addWorkLeaveWithoutPayFieldDefinitions,
    CSSZTranslationFiles,
    getConditionalFastEntryListRenderer,
    isDurationType,
    isEndType,
    isStartType,
    WorkedInDaysLocalPath
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import { hasOtherCareGiverBenefitClaim } from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionCaregiving.def";
import PrCSSZSubmissionLongTermCaregivingFormView
    from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionLongTermCaregivingFormView";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType } from "../../../../enums";
import {
    addFormDef,
    addFormGroup,
    addRowsToGroup,
    enhanceGroupFieldsVisibility
} from "../../../../views/formView/Form.utils";

const _detailPath = (path: PrCSSZSubmissionLongtermCaregivingDetailEntity, ...restProps: string[]) =>
    createPath(PrCSSZSubmissionLongtermCaregivingEntity.Detail, path, ...restProps);
const _paymentBasisPath = (path: PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity, ...restProps: string[]) =>
    _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.PaymentBasis, path, ...restProps);

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrCSSZSubmissionLongTermCaregivingFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication,
        subType: PrSubmissionSubtypeCode.NempriLongTermCaregiving
    }));

    // shared definitions with other CSSZ submissions
    addCommonNEMPRIDefinitions(d, context, args);

    // **************** CareGiving actions ****************
    addCaregivingActions(d, PrCSSZSubmissionLongtermCaregivingDetailEntity);

    // **************** Extend Employer confirmation group ****************
    // Student status
    const studyRows = addStudyFieldDefinitions(d, PrCSSZSubmissionLongtermCaregivingDetailEntity);
    // Work leave without pay
    const workLeaveWithoutPayRows = addWorkLeaveWithoutPayFieldDefinitions(d, PrCSSZSubmissionLongtermCaregivingDetailEntity);
    // Maternity related fields
    const maternityRelatedRows = addMaternityRelatedFieldDefinitions(d, PrCSSZSubmissionLongtermCaregivingDetailEntity);

    // Enhance group def
    const rows = [
        ...studyRows,
        ...workLeaveWithoutPayRows,
        ...maternityRelatedRows
    ];
    addRowsToGroup(d, PrSubmissionGroup.IncomeInPeriod, rows);
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.IncomeInPeriod, isStartType);
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.PaymentMethod, isStartType);

    // **************** BenefitRequest - fake empty group to show form title ****************
    addBasicBenefitRequestGroup(d, null, {
        forceVisibilityWithoutFields: true,
        isVisible: ifAny(isStartType, isDurationType, isEndType)
    });

    // **************** CareReceiver definition ****************
    const careReceiverRows = addCareReceiverDefinitions(d, _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.CareReceiver));
    addFormDef(d, _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.CareReceiverRelationship), {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XL,
        isRequired: true,
        fieldSettings: {
            displayName: PrMpsvFamilyRelationEntity.Name
        }
    });
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.DoesCareReceiverLiveInSameHousehold), "DoesCareReceiverLiveInSameHousehold");
    addFormGroup(d, {
        id: PrSubmissionGroup.CareReceiver,
        title: i18next.t("Submission:SocialEventsForm.CareReceiver"),
        showGroupDivider: false,
        rows: [
            ...careReceiverRows,
            [{ id: _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.CareReceiverRelationship) }],
            [{ id: _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.DoesCareReceiverLiveInSameHousehold) }]
        ]
    }, PrSubmissionGroup.BenefitRequest);
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.CareReceiver, isStartType);

    // **************** Caregiving Intervals ****************
    addCaregivingIntervalFields(d, PrCSSZSubmissionLongtermCaregivingDetailEntity, { labelPrefix: "LongTermCaregiving" });

    // **************** Additional benefit request fields ****************
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.HasOtherCareGiverBenefitClaim), "HasOtherCareGiverBenefitClaim", [
        _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.IsOtherCareGiverIncapacitated),
        _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.HasMultipleCaregivers)
    ], {
        isVisible: isStartType,
    });
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.IsOtherCareGiverIncapacitated), "IsOtherCareGiverIncapacitated", {
        isVisible: ifAll(isStartType, hasOtherCareGiverBenefitClaim)
    });
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.HasMultipleCaregivers), "HasMultipleCaregivers", {
        isVisible: ifAll(isStartType, hasOtherCareGiverBenefitClaim)
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitRequestFooter,
        showGroupDivider: false,
        rows: [[
            { id: _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.HasOtherCareGiverBenefitClaim) },
            { id: _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.IsOtherCareGiverIncapacitated) },
            { id: _detailPath(PrCSSZSubmissionLongtermCaregivingDetailEntity.HasMultipleCaregivers) },
        ]]
    }, PrSubmissionGroup.CaregivingIntervals);


    // **************** Benefit Payment Inputs group ****************
    const hasPlannedShiftsPath = _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.HasPlannedShifts);
    addCSSZSwitchDef(d, hasPlannedShiftsPath, "HasPlannedShifts", {
        isVisible: ifAny(isDurationType, isEndType),
    });
    // line items with dates...
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkScheduleDays, PrWorkedInDaysIntervalEntity.DateFrom), {
        label: i18next.t("Submission:SocialEventsForm.From"),
        isVisible: ifAny(isDurationType, isEndType),
    });
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkScheduleDays, PrWorkedInDaysIntervalEntity.DateTo), {
        label: i18next.t("Submission:SocialEventsForm.To"),
        isVisible: ifAny(isDurationType, isEndType),
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitPaymentInputsScheduledDays,
        title: i18next.t("Submission:SocialEventsForm.BenefitPaymentInputs"),
        isVisible: ifAny(isDurationType, isEndType),
        isSmallBottomMargin: true,
        rows: [
            [{ id: hasPlannedShiftsPath }],
        ],
        lineItems: {
            collection: _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkScheduleDays),
            order: PrWorkScheduleDaysIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => !!args.storage.getValueByPath(hasPlannedShiftsPath),
            columns: [
                { id: PrWorkScheduleDaysIntervalEntity.DateFrom },
                { id: PrWorkScheduleDaysIntervalEntity.DateTo }
            ],
            render: getConditionalFastEntryListRenderer(hasPlannedShiftsPath)
        }
    }, PrSubmissionGroup.BenefitRequestFooter);

    // LineItems - Worked in days
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkedInDays, PrWorkedInDaysIntervalEntity.DateFrom), {
        label: i18next.t("Submission:SocialEventsForm.From"),
        isVisible: ifAny(isDurationType, isEndType),
    });
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkedInDays, PrWorkedInDaysIntervalEntity.DateTo), {
        label: i18next.t("Submission:SocialEventsForm.To"),
        isVisible: ifAny(isDurationType, isEndType),
    });
    addCSSZSwitchDef(d, WorkedInDaysLocalPath, "EmployeeWorkedInDaysCaregiving");

    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitPaymentInputsWorkedInDays,
        isVisible: ifAny(isDurationType, isEndType),
        isSmallBottomMargin: true,
        rows: [
            [{ id: WorkedInDaysLocalPath }],
        ],
        lineItems: {
            collection: _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkedInDays),
            order: PrWorkedInDaysIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => !!args.storage.getValueByPath(WorkedInDaysLocalPath),
            columns: [
                { id: PrWorkedInDaysIntervalEntity.DateFrom },
                { id: PrWorkedInDaysIntervalEntity.DateTo }
            ],
            render: getConditionalFastEntryListRenderer(WorkedInDaysLocalPath)
        }
    }, PrSubmissionGroup.BenefitPaymentInputsScheduledDays);

    // LineItems - Worked Leave days
    const hasWorkLeavePath = _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.HasWorkLeave);
    addCSSZSwitchDef(d, hasWorkLeavePath, "HasWorkLeave", {
        isVisible: ifAny(isDurationType, isEndType),
    });
    // line items with dates...
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkLeaveDays, PrWorkedInDaysIntervalEntity.DateFrom), {
        label: i18next.t("Submission:SocialEventsForm.From"),
        isVisible: ifAny(isDurationType, isEndType),
    });
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkLeaveDays, PrWorkedInDaysIntervalEntity.DateTo), {
        label: i18next.t("Submission:SocialEventsForm.To"),
        isVisible: ifAny(isDurationType, isEndType),
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitPaymentInputs,
        isVisible: ifAny(isDurationType, isEndType),
        isSmallBottomMargin: true,
        rows: [
            [{ id: hasWorkLeavePath }],
        ],
        lineItems: {
            collection: _paymentBasisPath(PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkLeaveDays),
            order: PrWorkLeaveDaysIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => !!args.storage.getValueByPath(hasWorkLeavePath),
            columns: [
                { id: PrWorkLeaveDaysIntervalEntity.DateFrom },
                { id: PrWorkLeaveDaysIntervalEntity.DateTo }
            ],
            render: getConditionalFastEntryListRenderer(hasWorkLeavePath)
        }
    }, PrSubmissionGroup.BenefitPaymentInputsWorkedInDays);

    // **************** Benefit Payment Inputs - return to work ****************
    addReturnToWorkFields(d, PrCSSZSubmissionLongtermCaregivingDetailEntity, { addDateReturnToWork: true });
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.BenefitPaymentInputsReturnToWork, isEndType);

    return {
        ...d,
        entitySet: EntitySetName.PrCSSZSubmissionsLongtermCaregiving
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrCSSZSubmissionLongtermCaregiving, getDefinitions);